'use client'

import {
    circuitBannerCopy,
    useCircuitBreakerStatus,
} from '@/lib/features/network-status/circuitBreakerConsumer'

/**
 * T-NOTIFIER-1 (2026-04-19).
 *
 * Root-level banner rendered when one or more hosts have tripped
 * their circuit breaker OPEN. Reads from the same
 * `useCircuitBreakerStatus()` hook the network-status-banner spec
 * invariants test against, so the in-production surface matches the
 * reconciliation-test assertions bit-for-bit.
 *
 * Mounted above `{children}` in `src/app/layout.tsx` so every
 * authenticated flow gets the banner. The component returns `null`
 * when the breaker set is empty — zero DOM cost when everything
 * is healthy.
 *
 * Copy comes from `circuitBannerCopy(hosts)` — the centralised
 * helper also used by the web reconciliation test at
 * `src/test/reconciliation/network-status-banner.reconciliation.test.ts`.
 */
export function NetworkStatusBanner() {
    const { degraded, hosts } = useCircuitBreakerStatus()
    if (!degraded) return null

    const { title, detail } = circuitBannerCopy(hosts)

    return (
        <div
            role="status"
            aria-live="polite"
            className="fixed top-0 inset-x-0 z-[100] bg-amber-600/95 text-white text-sm px-4 py-2 shadow-md backdrop-blur-sm flex items-center justify-center gap-2"
        >
            <span className="font-semibold">{title}</span>
            <span className="opacity-90">{detail}</span>
        </div>
    )
}
